import { PartitionOutlined } from '@ant-design/icons'
import { getCurrentMarket, setCurrentMarket, jsonSafeParse } from '@utils/general'
import { Select, TreeSelect } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { defaultSiteMonitoringFilters } from '../StoreMonitoring/SiteMonitoringContext'
import { useUserSettings } from '@hooks/settings'
import { useSecurityContext } from '@components/Navigation/SecurityContext'

const { Option } = Select
const { SHOW_PARENT } = TreeSelect

const store = window.localStorage

export function MarketSelect({ reloadOnSectorChange = true, onSectorChange, marketData }) {
  const { scopes } = useSecurityContext()
  const [market, setMarket] = useState(getCurrentMarket() || '')
  const [options, setOptions] = useState([])
  const { updateSetting } = useUserSettings()

  const onChange = async (value) => {
    const marketSectors = marketData?.rows.find((m) => m.name === value)?.sectors
    const serializedSectorDefaults = JSON.stringify(marketSectors?.map((s) => s.name))

    setMarket(value)
    store.removeItem('driver-schedule-depots')
    await updateSetting('driver-schedule-depots', '')
    store.setItem('order-dispatch-sectors', serializedSectorDefaults)
    await updateSetting('order-dispatch-sectors', serializedSectorDefaults)
    onSectorChange?.(value)

    if (reloadOnSectorChange) {
      store.removeItem('site-monitoring-custom-search') // remove the custom fe search filter on site monitoring
      await updateSetting('site-monitoring-custom-search', '')

      // keep all server side filters sticky except for sectors when the market changes
      const storageKey = 'site_monitoring_server_query_filters'

      const currentFilters = jsonSafeParse(store.getItem(storageKey)) ?? defaultSiteMonitoringFilters
      // throw away sectors and market

      const { sectors, market, ...rest } = currentFilters
      store.setItem(storageKey, JSON.stringify(rest))
      updateSetting(storageKey, rest).then()
      window.location.reload()
    }
  }

  useEffect(() => {
    const sortOptions = (a, b) => {
      const orderBool = a.text.toUpperCase() > b.text.toUpperCase()
      return orderBool ? 1 : -1
    }
    if (marketData?.rows) {
      const list = scopes.market_names?.length
        ? marketData?.rows.filter((m) => scopes.market_names.includes(m.name))
        : marketData?.rows
      const actualOptions = list.map((row) => {
        const text = row.profile ? `${row.name} (${row.profile?.name})` : row.name
        return {
          text,
          value: row.name,
        }
      })
      if (!scopes.market_names?.length) {
        actualOptions.push({ text: 'All', value: '' })
      }
      actualOptions.sort(sortOptions)
      setOptions(actualOptions)
    }
  }, [marketData?.rows, scopes?.market_names])

  useEffect(() => {
    if (scopes?.market_names?.length && !scopes.market_names?.includes(market)) {
      setCurrentMarket(scopes.market_names[0])
      setMarket(scopes.market_names[0])
      updateSetting('currentMarket', scopes.market_names[0]).then()
    }
  }, [scopes?.market_names, market])

  return (
    <Select
      onChange={(value) => {
        setCurrentMarket(value)
        updateSetting('currentMarket', value).then(() => onChange(value))
      }}
      placeholder='Viewing All Markets'
      showSearch
      className='market-select'
      value={market}
      style={{ width: '100%' }}
    >
      {options &&
        options.map((d, i) => (
          <Option value={d.value} key={i}>
            {d.text}
          </Option>
        ))}
    </Select>
  )
}

export function SectorMultiSelect({ selectedValues, sectors, handleSectorChange, style, size, byMarket }) {
  const placeholder = (
    <div className='flex items-center'>
      <PartitionOutlined className='pr-2' />
      Sectors
    </div>
  )

  if (!sectors || sectors.length === 0)
    return <TreeSelect multiple style={style} size='small' placeholder={placeholder} />
  return (
    <SectorTree
      placeholder={placeholder}
      sectors={sectors}
      byMarket={byMarket}
      handleSectorChange={handleSectorChange}
      selectedValues={selectedValues}
      size={size}
      style={style}
    />
  )
}

function SectorTree({ placeholder, sectors, byMarket, handleSectorChange, selectedValues, style }) {
  const treeData = useMemo(() => {
    return sectors?.map((m) => ({
      title: m.name.toUpperCase(),
      value: m.name,
      key: m.name,
      children: m.sectors
        .filter((s) => s.name !== 'default')
        .map((s) => ({
          title: s.name.toUpperCase(),
          value: s.name,
          key: s.name,
        })),
    }))
  }, [sectors, byMarket])

  return (
    <TreeSelect
      treeData={treeData}
      showCheckedStrategy={SHOW_PARENT}
      multiple
      allowClear
      treeDefaultExpandAll
      treeCheckable
      placeholder={placeholder}
      value={selectedValues}
      onChange={(value) => handleSectorChange(value, sectors)}
      showSearch
      style={style}
      treeNodeFilterProp='title'
      filterTreeNode={(inputValue, treeNode) => {
        if (!inputValue) return true // Show all nodes when input is empty
        return treeNode.title.toLowerCase().includes(inputValue.toLowerCase())
      }}
    />
  )
}
